import { useQuery } from '@tanstack/react-query';
import { Bug, CheckSquare, FileText, FolderKanban, ListTodo, Search, User } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { searchApi, type SearchResults } from '@/lib/discovery-api';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';

const RECENT_STORAGE = 'jira.recent-searches';

type Item =
  | { kind: 'issue'; id: string; label: string; sub: string; path: string }
  | { kind: 'project'; id: string; label: string; sub: string; path: string }
  | { kind: 'user'; id: string; label: string; sub: string; path: string };

function issueIcon(type: 'STORY' | 'TASK' | 'BUG' | 'SUBTASK') {
  switch (type) {
    case 'STORY':
      return <FileText className="h-4 w-4 text-emerald-600" aria-hidden />;
    case 'TASK':
      return <ListTodo className="h-4 w-4 text-sky-600" aria-hidden />;
    case 'BUG':
      return <Bug className="h-4 w-4 text-rose-600" aria-hidden />;
    case 'SUBTASK':
      return <CheckSquare className="h-4 w-4 text-slate-500" aria-hidden />;
    default:
      return <FileText className="h-4 w-4" aria-hidden />;
  }
}

function resultsToItems(results: SearchResults | null | undefined, orgSlug: string | null): Item[] {
  if (!results) return [];
  const items: Item[] = [];
  for (const issue of results.issues) {
    const slug = issue.projectOrgSlug ?? orgSlug ?? '';
    items.push({
      kind: 'issue',
      id: `issue:${issue.id}`,
      label: `${issue.key} · ${issue.title}`,
      sub: `${issue.projectKey} · ${issue.matchType === 'KEY' ? 'exact key match' : 'matches title/description'}`,
      path: `/o/${slug}/projects/${issue.projectKey}/issues/${issue.key}`,
    });
  }
  for (const project of results.projects) {
    const slug = project.orgSlug ?? orgSlug ?? '';
    items.push({
      kind: 'project',
      id: `project:${project.id}`,
      label: project.name,
      sub: `${project.key}${project.description ? ` · ${project.description}` : ''}`,
      path: `/o/${slug}/projects/${project.key}`,
    });
  }
  for (const user of results.users) {
    items.push({
      kind: 'user',
      id: `user:${user.id}`,
      label: user.name,
      sub: user.email,
      path: `/profile`,
    });
  }
  return items;
}

export function CommandPalette() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState<string[]>([]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const membership = user?.memberships.find((m) => m.organizationId === activeOrgId);
  const orgSlug = membership?.organizationSlug ?? user?.memberships[0]?.organizationSlug ?? null;

  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = window.localStorage.getItem(RECENT_STORAGE);
      setRecent(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setRecent([]);
    }
    setQuery('');
    setActiveIndex(0);
    // Focus is handled automatically by Radix Dialog's auto-focus of the first focusable child;
    // still, schedule an explicit focus for safety.
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  const searchQuery = useQuery({
    queryKey: ['search', activeOrgId, debounced],
    queryFn: () => searchApi(activeOrgId!, debounced),
    enabled: open && Boolean(activeOrgId) && debounced.length >= 2,
  });

  const items = useMemo(
    () => resultsToItems(searchQuery.data, orgSlug),
    [searchQuery.data, orgSlug],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = items[activeIndex];
      if (picked) selectItem(picked);
    }
  };

  const selectItem = (item: Item) => {
    // Save the *query* that led to this selection to recent searches.
    if (debounced) {
      try {
        const next = [debounced, ...recent.filter((r) => r !== debounced)].slice(0, 10);
        window.localStorage.setItem(RECENT_STORAGE, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    navigate(item.path);
  };

  const isShort = debounced.length > 0 && debounced.length < 2;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 sm:max-w-2xl" aria-label="Command palette">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search issues, projects, and people…"
            className="border-0 shadow-none focus-visible:ring-0"
            role="combobox"
            aria-expanded={open}
            aria-controls="cmdk-results"
            aria-activedescendant={items[activeIndex]?.id}
          />
          <kbd className="hidden rounded border bg-muted/50 px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline">
            Esc
          </kbd>
        </div>
        <div
          id="cmdk-results"
          role="listbox"
          className="max-h-96 overflow-y-auto"
          aria-label="Search results"
        >
          {isShort ? (
            <p className="px-4 py-8 text-center text-xs text-muted-foreground">
              Type at least 2 characters to search.
            </p>
          ) : debounced.length === 0 ? (
            <RecentSearches recent={recent} onPick={(q) => setQuery(q)} />
          ) : searchQuery.isLoading ? (
            <p className="px-4 py-8 text-center text-xs text-muted-foreground">Searching…</p>
          ) : searchQuery.isError ? (
            <p className="px-4 py-8 text-center text-xs text-destructive">
              Could not search right now.
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-muted-foreground">No results.</p>
          ) : (
            <ul>
              {items.map((item, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <li
                    key={item.id}
                    id={item.id}
                    role="option"
                    aria-selected={isActive}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => selectItem(item)}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 px-3 py-2 text-sm',
                      isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                    )}
                  >
                    {item.kind === 'issue' ? (
                      issueIcon(
                        (searchQuery.data?.issues.find((i) => `issue:${i.id}` === item.id)?.type ??
                          'TASK') as 'STORY' | 'TASK' | 'BUG' | 'SUBTASK',
                      )
                    ) : item.kind === 'project' ? (
                      <FolderKanban className="h-4 w-4 text-muted-foreground" aria-hidden />
                    ) : (
                      <User className="h-4 w-4 text-muted-foreground" aria-hidden />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{item.label}</div>
                      <div className="truncate text-xs text-muted-foreground">{item.sub}</div>
                    </div>
                    <span className="shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {item.kind}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecentSearches({ recent, onPick }: { recent: string[]; onPick: (q: string) => void }) {
  if (recent.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-xs text-muted-foreground">
        Search issues by key (e.g. <span className="font-mono">ENG-42</span>), title, or mention.
      </p>
    );
  }
  return (
    <div className="px-2 py-2">
      <p className="px-2 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Recent</p>
      <ul>
        {recent.map((q) => (
          <li key={q}>
            <button
              type="button"
              onClick={() => onPick(q)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              <Search className="h-3 w-3 text-muted-foreground" aria-hidden />
              <span>{q}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
